import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { selectPaymentScheduleData } from '../../../redux/paymentScheduleDataSlice'
import { DateFormat } from '../../../helpers/formatting'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'

export default function UnholdPaymentModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const [saving, setSaving] = useState(false)
  const paymentId = modal.options?.payment_id;
  const loanId = modal.options?.loan_id;
  const [isBeforeCutoff, setIsBeforeCutoff] = useState(null)
  const [isFuturePayment, setIsFuturePayment] = useState(false)
  const [isPastPayment, setIsPastPayment] = useState(false)
  const [paymentDate, setPaymentDate] = useState(null)
  const dropdownOptions = useSelector(selectDropdownOptions)

  const request = useRequest()
  const paymentScheduleData = useSelector(selectPaymentScheduleData)
  const currentDate = paymentScheduleData.paymentScheduleCurrentDate

  const active = modal.is_active && modal.type === 'unhold_payment_modal'
  const requestedPaymentId = useRef(null)

  const resetState = () => {
    requestedPaymentId.current = null
    setIsBeforeCutoff(null)
    setIsFuturePayment(false)
    setIsPastPayment(false)
    setPaymentDate(null)
    setSaving(false)
  }

  // This modal stays mounted in the Footer between openings. Without clearing, the previous
  // payment's cutoff/past/future branch renders live Yes/No buttons for this payment until the new
  // check returns, so a click would unhold this payment with the other payment's decision.
  useEffect(() => {
    if (active && paymentId) {
      resetState()
      checkSameDayCutoff()
    }
  }, [active, paymentId])

  const checkSameDayCutoff = async () => {
    const forPaymentId = paymentId
    const isStale = () => requestedPaymentId.current !== forPaymentId
    requestedPaymentId.current = forPaymentId
    const params = new URLSearchParams()
    params.append('payment_id', forPaymentId)
    if (currentDate) {
      params.append('current_date', currentDate)
    }
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/check-same-day-cutoff?${params.toString()}`)
    try {
      const response = await request.get()
      if (isStale()) return
      if (response.status === 200 || response.status === 201) {
        setIsBeforeCutoff(response.data?.data?.is_inside_ach_cutoff)
        setIsFuturePayment(response.data?.data?.is_future_payment ?? false)
        setIsPastPayment(response.data?.data?.is_past_payment ?? false)
        setPaymentDate(response.data?.data?.payment_date ?? false)
      } else {
        setIsBeforeCutoff(null)
      }
    } catch (error) {
      if (isStale()) return
      setIsBeforeCutoff(null)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleSubmit = async (newPaymentStatus, scheduleToNextBusinessDay = false) => {
    setSaving(true)
    request.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/payment/${paymentId}/unhold`
    )
    try {
      let params = { new_payment_status_dropdown_option_id: dropdownOptions.filter((o) => o.code_key === newPaymentStatus)[0].id }
      if (currentDate !== null) {
        params.current_date = currentDate;
      }
      if (scheduleToNextBusinessDay) {
        params.schedule_to_next_business_day = true;
      }
      const response = await request.modify(params)
      if (response.status === 200) {
        setSaving(false)
        handleCancel();
        modal.options.successCallback();
        dispatch(setNotification({ type: 'success', body: '' }))
      } else {
        setSaving(false)
        dispatch(
          setNotification({ type: 'error', body: `Error: ${response.status}` })
        )
      }
    } catch (error) {
      setSaving(false)
      const errorMessage =
        error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const markAsSchedule = async (scheduleToNextBusinessDay = false) => {
    await handleSubmit('payment_status_scheduled', scheduleToNextBusinessDay)
  }

  const markAsMissed = async () => {
    await handleSubmit('payment_status_missed')
  }

  const handleCancel = () => {
    resetState()
    dispatch(setModal({ is_active: false }))
  };

  const generateContent = (element) => {
    if (isBeforeCutoff === null) {
      return <span className="has-text-center"><Loading /></span>;
    } else if (isPastPayment) {
        //past payment
        if (element === 'button') {
          return (
            <>
              <button
                type="submit"
                className="button is-small is-link mx-1"
                onClick={() => markAsMissed()}
                disabled={saving}
              >Yes
              </button>
              <button
                type="button"
                className="button is-small has-background-grey has-text-white mx-1"
                onClick={handleCancel}
              >No
              </button>
            </>

          );
        } else {
          return (
            <h3>
              Unholding this payment (ID: {paymentId}) will move the status to missed, are you sure you want to do this?
            </h3>
          )
        }
    } else if (isFuturePayment) {
        if (element === 'button') {
          return (
            <>
              <button
                type="submit"
                className="button is-small is-link mx-1"
                onClick={() => markAsSchedule()}
                disabled={saving}
              >Yes
              </button>
              <button
                type="button"
                className="button is-small has-background-grey has-text-white mx-1"
                onClick={handleCancel}
              >No
              </button>
            </>

          );
        } else {
          return (
            <h3>
              Are you sure you want to mark this payment (ID: {paymentId}) as scheduled? This action will confirm that
              the payment is set for processing ({paymentDate && <DateFormat dateString={paymentDate} />})
            </h3>
          );
        }
    } else if (!isPastPayment && !isFuturePayment) {
        if (isBeforeCutoff) {
            //same day and before cutoff
            // eslint-disable-next-line no-lonely-if
            if (element === 'button') {
              return (
                <>
                  <button
                    type="submit"
                    className="button is-small is-link mx-1"
                    onClick={() => markAsSchedule()}
                    disabled={saving}
                  >Yes
                  </button>
                  <button
                    type="button"
                    className="button is-small has-background-grey has-text-white mx-1"
                    onClick={handleCancel}
                  >No
                  </button>
                </>
              )
            } else {
              return (
                <h3>
                  Are you sure you want to mark this payment (ID: {paymentId}) as scheduled?
                  This action will confirm that the payment is set for processing today.
                </h3>
              );
            }
        } else {
          //after cutoff
          // eslint-disable-next-line no-lonely-if
          if (element === 'button') {
            return (
              <>
                <button
                  type="submit"
                  className="button is-small is-link mx-1"
                  onClick={() => markAsSchedule(true)}
                  disabled={saving}
                >Yes
                </button>
                <button
                  type="button"
                  className="button is-small has-background-grey has-text-white mx-1"
                  onClick={() => markAsMissed()}
                  disabled={saving}
                >No
                </button>
                <button
                  type="button"
                  className="button is-small has-background-grey has-text-white mx-1"
                  onClick={() => handleCancel()}
                >Cancel
                </button>
              </>

            );
          } else {
            return (
              <h3>
                Same Day Cutoff has passed. The payment (ID: {paymentId}) cannot be processed today. Would you like to
                schedule the payment for the next business day instead?
              </h3>
            );
          }
        }
    }
  }

  return modal.is_active && modal.type === 'unhold_payment_modal' ? (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card modal-card-confirmation column is-7">
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">
            {"Unhold Payment"}
          </p>
          <button className="delete" aria-label="close" onClick={handleCancel}></button>
        </header>
        <section
          className="modal-card-body modal-card-body pt-1 pb-1"
          style={{ minHeight: "30px", overflow: 'hidden' }}
        >
          <div className="columns mb-0 is-flex-direction-column">
            <div className="column">
              {generateContent('modelContent')}
            </div>
          </div>
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1 pr-1">
          <div className="lox-form__buttons-wrapper has-text-right">
            {generateContent('button')}
          </div>
        </footer>
      </div>
    </div>
  ) : null;
}
